import type { ScheduleItem } from '../types/index.js'

/**
 * « Lancer la source maintenant » côté régie — miroir de
 * `backend/src/lib/live-start-now.ts`, le serveur reste seul juge.
 *
 * Un direct lançable est en ERREUR, ou encore en attente (`SCHEDULED` /
 * `LOADING`) alors que son heure de grille est passée, et il lui reste au moins
 * 10 s. Le 01/10/2026, le direct en ERREUR occupait son créneau : la régie
 * affichait ses boutons de direct (« Fin du direct », « Décrocher »), que le
 * serveur refusait tous, faute d'élément à l'antenne.
 */

export const LIVE_START_NOW_MIN_REMAINING_MS = 10_000
/**
 * Un direct en attente n'est proposé qu'après ce retard : le moteur le lance
 * lui-même à l'heure, la régie ne doit pas afficher le bandeau d'impasse pendant
 * la seconde où l'état de grille rattrape la bascule.
 */
export const LIVE_START_NOW_GRACE_MS = 5_000

const ms = (value: string) => new Date(value).getTime()

export function isLiveStartable(item: ScheduleItem, nowMs: number): boolean {
  if (item.itemType !== 'LIVE') return false
  if (ms(item.endTime) - nowMs < LIVE_START_NOW_MIN_REMAINING_MS) return false
  if (item.status === 'ERROR') return ms(item.startTime) <= nowMs
  if (item.status === 'SCHEDULED' || item.status === 'LOADING') {
    return nowMs - ms(item.startTime) >= LIVE_START_NOW_GRACE_MS
  }
  return false
}

/**
 * Direct à proposer : le plus ancien direct lançable dont le créneau couvre
 * l'instant présent. Aucun si un direct est déjà réellement à l'antenne.
 */
export function findStartableLive(items: readonly ScheduleItem[], nowMs: number): ScheduleItem | null {
  if (items.some(item => item.itemType === 'LIVE' && item.status === 'ON_AIR')) return null
  return [...items]
    .filter(item => isLiveStartable(item, nowMs))
    .sort((a, b) => ms(a.startTime) - ms(b.startTime))[0] ?? null
}
